/** Noise Extraction SimEvent kinds — as const map + derived union. */

export const NeEvent = {
  RunStarted: "run_started",
  LootCollected: "loot_collected",
  AlertChanged: "alert_changed",
  HoldStarted: "hold_started",
  HoldCancelled: "hold_cancelled",
  RunEnded: "run_ended",
  NoiseBurst: "noise_burst",
  Footstep: "footstep",
  PursuerSonar: "pursuer_sonar",
  /** Player-caused sonar cast (idle / hold isotropic / wall scrape). */
  PlayerSonar: "player_sonar",
} as const;

export type NeEventKind = (typeof NeEvent)[keyof typeof NeEvent];

/** Player HUD alert — pursuer was hit by player-caused sonar (≥1). */
export type NeAlertCause = "sonar_hit";

export type NeRunResult = "escaped" | "caught";

export type NeHoldKind = "loot" | "exit";

export type NePursuerMode =
  | "search"
  | "investigate"
  | "sweep"
  | "chase"
  | "catching";

/** Player sonar rail chroma — loot hold uses rarity identity (not player blue). */
export type NeSonarTint =
  | "player"
  | "orange"
  | "hold"
  | "loot_low"
  | "loot_high"
  /** Soft isotropic presence ring (idle/move; not foot lobe). */
  | "heartbeat";

/** Pursuer sonar pulse family — stride lobe vs short heartbeat ring. */
export type NePursuerPulse = "stride" | "heartbeat";

export type NoiseExtractionEvent =
  | {
      kind: typeof NeEvent.RunStarted;
      seed: number;
      messageKey: "noise.msg.runStarted";
    }
  | {
      kind: typeof NeEvent.LootCollected;
      tier: "low" | "high";
      value: number;
      noise: number;
      elapsedSec: number;
      burstRadius: number;
      messageKey: "noise.msg.lootLow" | "noise.msg.lootHigh";
    }
  | {
      kind: typeof NeEvent.AlertChanged;
      from: number;
      to: number;
      cause: NeAlertCause;
      elapsedSec: number;
      messageKey: "noise.msg.alertSonar";
    }
  | {
      kind: typeof NeEvent.HoldStarted;
      holdKind: NeHoldKind;
      lootId: string | null;
      carryingValue: number;
      alertTier: number;
      elapsedSec: number;
      messageKey: "noise.msg.extracting" | "noise.msg.looting";
    }
  | {
      kind: typeof NeEvent.HoldCancelled;
      holdKind: NeHoldKind;
      messageKey: "noise.msg.extractCancel" | "noise.msg.lootCancel";
    }
  | {
      kind: typeof NeEvent.RunEnded;
      result: NeRunResult;
      carryingValue: number;
      maxAlertTier: number;
      highLootCount: number;
      elapsedSec: number;
      messageKey: "noise.result.escaped" | "noise.result.caught";
    }
  | {
      kind: typeof NeEvent.NoiseBurst;
      x: number;
      y: number;
      radius: number;
      isotropic: boolean;
      messageKey: "noise.msg.burst";
    }
  | {
      kind: typeof NeEvent.Footstep;
      x: number;
      y: number;
      facingX: number;
      facingY: number;
      sideAmp: number;
      forwardPower: number;
      scale: number;
      /** -1 | 1 — alternate foot for sonar origin sway. */
      footSide: number;
      /** True every N footsteps — Scene casts a move sonar from this step. */
      emitSonar: boolean;
      messageKey: "noise.msg.burst";
    }
  | {
      kind: typeof NeEvent.PursuerSonar;
      pursuerId: string;
      /** Snapshot at emit — Scene picks rail color from this (not sticky alert). */
      mode: NePursuerMode;
      /** stride = L/R foot lobe; heartbeat = short isotropic presence. */
      pulse: NePursuerPulse;
      x: number;
      y: number;
      facingX: number;
      facingY: number;
      sideAmp: number;
      forwardPower: number;
      scale: number;
      footSide: number;
      messageKey: "noise.msg.burst";
    }
  | {
      kind: typeof NeEvent.PlayerSonar;
      x: number;
      y: number;
      facingX: number;
      facingY: number;
      sideAmp: number;
      forwardPower: number;
      scale: number;
      footSide: number;
      isotropic: boolean;
      tint: NeSonarTint;
      lifeMul: number;
      maxRange: number;
      rayCount: number;
      /**
       * Loot hold shell emit — origins on circumference, outward normals.
       * Null = cast from (x,y) as usual (stride / scrape / exit hold).
       */
      rimEmit: { cx: number; cy: number; radius: number } | null;
      messageKey: "noise.msg.burst";
    };
